/**
 * @fileoverview "Narrow it down": the Plant Doctor's follow-up questions.
 *
 * Moved word for word out of PlantDoctor (audit batch 18, A107).
 *
 * Where: PlantDoctor (the window), under the answer or alone while the server holds an answer back.
 * Words (en/es): plants.diagnose.narrow* (narrowHeading, narrowMoreHeading, narrowTap,
 * narrowProgress, narrowSend, narrowNeedAll); sitewide.sending (Button loading, busy="sending") while the answers go.
 * B111: no Cancel here (the dialog's X ends the conversation); the send button waits as the shared Button's loading.
 *
 * @module FollowUp
 */

import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { FollowUpQuestion } from '@/shared/api/diagnose';
import { DoctorProgress } from '../plant-list/components/card/DoctorProgress';
import { QUIET } from './doctorStyles';

/** What FollowUp is given. */
interface FollowUpProps {
  /** This round's questions, each with its answers. */
  questions: FollowUpQuestion[];
  /** True after the first round: "a little more". */
  again: boolean;
  /** Sending the taps. */
  busy: boolean;
  /** True once the updated answer has arrived: the waiting steps finish. */
  ready: boolean;
  /** Called when the waiting steps have finished after the answer arrived. */
  onProgressDone: () => void;
  /** The plant's name. */
  plantName: string;
  /** Sends one answer per question. */
  onSend: (answers: Array<{ id: string; answer: string }>) => void;
}

/**
 * "Narrow it down · Tap an answer to each question": open, right under the
 * answer. Each question's answers are buttons (outlined, 44px tall, filled
 * in the theme's primary when chosen), a count says how many are answered,
 * and "Update the answer" waits until every question has one, saying so.
 *
 * @param props - FollowUpProps.
 */
export const FollowUp: React.FC<FollowUpProps> = ({ questions, again, busy, ready, onProgressDone, plantName, onSend }) => {
  const { reactTranslate: t } = useTranslation();
  const [picked, setPicked] = React.useState<Record<string, string>>({});
  const answered = questions.filter((q) => picked[q.id] !== undefined).length;
  const allAnswered = answered === questions.length;
  const headingId = React.useId();
  const hintId = React.useId();

  return (
    <section aria-labelledby={headingId} className="space-y-4 rounded-md border-2 border-primary/40 bg-card p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 id={headingId} className="text-base font-semibold">
          {t(again ? 'plants.diagnose.narrowMoreHeading' : 'plants.diagnose.narrowHeading')}
          <span className={cn('font-normal', QUIET)}> · {t('plants.diagnose.narrowTap')}</span>
        </h3>
        <p className={cn('text-xs font-medium', QUIET)} aria-live="polite">
          {t('plants.diagnose.narrowProgress', { answered, count: questions.length })}
        </p>
      </div>
      {questions.map((question) => (
        <fieldset key={question.id} className="space-y-2" disabled={busy}>
          <legend className="mb-2 text-sm font-medium">{question.text}</legend>
          <div className="flex flex-wrap gap-2">
            {question.options.map((option) => {
              const on = picked[question.id] === option;
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setPicked((p) => ({ ...p, [question.id]: option }))}
                  className={cn(
                    'inline-flex min-h-11 items-center gap-1.5 rounded-md border-2 px-4 text-sm font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:translate-y-px disabled:opacity-60',
                    on
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                      : 'border-foreground/40 bg-background text-foreground shadow-sm hover:border-primary hover:bg-muted active:bg-muted',
                  )}
                >
                  {on && <Check className="h-4 w-4" aria-hidden="true" />}
                  {option}
                </button>
              );
            })}
          </div>
        </fieldset>
      ))}
      <div className="space-y-1.5">
        <Button
          onClick={() => onSend(Object.entries(picked).map(([id, answer]) => ({ id, answer })))}
          loading={busy}
          busy="sending"
          disabled={!allAnswered}
          aria-describedby={allAnswered ? undefined : hintId}
          className="min-h-11"
        >
          {t('plants.diagnose.narrowSend')}
        </Button>
        {busy && <DoctorProgress kind="followUp" plantName={plantName} forecastUsed={false} ready={ready} onDone={onProgressDone} />}
        {!allAnswered && !busy && <p id={hintId} className={cn('text-xs', QUIET)}>{t('plants.diagnose.narrowNeedAll')}</p>}
      </div>
    </section>
  );
};
